import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../services/api/apiService';
import { useImages } from '../context/images/ImagesContext';
import WebhookService from '../services/api/webhookService';
import TrainingService from '../services/api/trainingService';

const TrainingSettingsPage = () => {
  const { selectedInfluencer } = useImages();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [zipSnackbarOpen, setZipSnackbarOpen] = useState(false);
  const [zipSnackbarMessage, setZipSnackbarMessage] = useState('');
  const [zipSnackbarSeverity, setZipSnackbarSeverity] = useState('success');
  
  // Autotrainer settings
  const [autotrainerSettings, setAutotrainerSettings] = useState({
    name: '',
    trigger: '',
    validation_prompt: '',
    checkpoint: 'level_2_v3/',
    steps: 15000,
    rank: 64,
    validation_steps: 1000,
    resolution: '1024x1024',
    project: 'test',
    lora_type: 'lycoris',
    learning_rate: 0.0001
  });
  
  // Image processing settings (replaces deprecated cropper/captioner)
  const [imageProcessingSettings, setImageProcessingSettings] = useState({
    min_size: 512,
    max_size: 2048,
    quality_threshold: 0.7,
    auto_crop: true,
    auto_caption: true,
    caption_template: selectedInfluencer ? `A photo of ${selectedInfluencer.login}` : 'A photo'
  });
  
  // Load settings when influencer changes
  useEffect(() => {
    if (selectedInfluencer) {
      fetchTrainingSettings();
      fetchValidationData();
    }
  }, [selectedInfluencer]);
  
  const fetchTrainingSettings = async () => {
    if (!selectedInfluencer) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/api/training/settings?influencerId=${selectedInfluencer.id}`);
      
      if (response.data) {
        // Set autotrainer settings
        if (response.data.autotrainer) {
          setAutotrainerSettings({
            name: response.data.autotrainer.name || `${selectedInfluencer.login}_level_2_lokr_lr=1e-4_final`,
            trigger: response.data.autotrainer.trigger || selectedInfluencer.login,
            validation_prompt: response.data.autotrainer.validation_prompt || `${selectedInfluencer.login}, standing on beach, topless`,
            checkpoint: response.data.autotrainer.checkpoint || 'level_2_v3/',
            steps: response.data.autotrainer.steps || 15000,
            rank: response.data.autotrainer.rank || 64,
            validation_steps: response.data.autotrainer.validation_steps || 1000,
            resolution: response.data.autotrainer.resolution || '1024x1024',
            project: response.data.autotrainer.project || 'test',
            lora_type: response.data.autotrainer.lora_type || 'lycoris',
            learning_rate: response.data.autotrainer.learning_rate || 0.0001
          });
        } else {
          // Set default values based on influencer
          setAutotrainerSettings({
            ...autotrainerSettings,
            name: `${selectedInfluencer.login}_level_2_lokr_lr=1e-4_final`,
            trigger: selectedInfluencer.login,
            validation_prompt: `${selectedInfluencer.login}, standing on beach, topless`
          });
        }
        
        // Set image processing settings (replaces deprecated cropper/captioner)
        if (response.data.image_processing) {
          setImageProcessingSettings({
            min_size: response.data.image_processing.min_size || 512,
            max_size: response.data.image_processing.max_size || 2048,
            quality_threshold: response.data.image_processing.quality_threshold || 0.7,
            auto_crop: response.data.image_processing.auto_crop !== undefined ? response.data.image_processing.auto_crop : true,
            auto_caption: response.data.image_processing.auto_caption !== undefined ? response.data.image_processing.auto_caption : true,
            caption_template: response.data.image_processing.caption_template || `A photo of ${selectedInfluencer.login}`
          });
        } else {
          // Set default values based on influencer
          setImageProcessingSettings({
            ...imageProcessingSettings,
            caption_template: `A photo of ${selectedInfluencer.login}`
          });
        }
      }
    } catch (error) {
      console.error('Error fetching training settings:', error);
      
      // Try to load from localStorage as fallback
      try {
        const storedSettings = localStorage.getItem(`training_settings_${selectedInfluencer.id}`);
        if (storedSettings) {
          const settings = JSON.parse(storedSettings);
          
          // Set autotrainer settings
          if (settings.autotrainer) {
            setAutotrainerSettings(settings.autotrainer);
          }
          // Set image processing settings
          if (settings.image_processing) {
            setImageProcessingSettings(settings.image_processing);
          }
        }
      } catch (localError) {
        console.error('Error loading from localStorage:', localError);
      }
      
      // Don't show error for 404, just use defaults
      if (error.response?.status !== 404) {
        setError('Failed to load training settings from server');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleAutotrainerChange = (e) => {
    const { name, value } = e.target;
    setAutotrainerSettings({
      ...autotrainerSettings,
      [name]: name === 'steps' || name === 'rank' || name === 'validation_steps' 
        ? parseInt(value, 10) 
        : name === 'learning_rate' 
          ? parseFloat(value)
          : value
    });
  };
  
  const handleImageProcessingChange = (e) => {
    const { name, value, type, checked } = e.target;
    setImageProcessingSettings({
      ...imageProcessingSettings,
      [name]: type === 'checkbox' ? checked :
              name === 'min_size' || name === 'max_size' ? parseInt(value, 10) :
              name === 'quality_threshold' ? parseFloat(value) :
              value
    });
  };
  
  const [autoTraining, setAutoTraining] = useState(false);
  
  // Validation state
  const [validationData, setValidationData] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationError, setValidationError] = useState(null);
  
  // Force training state
  const [forceTrainingDialog, setForceTrainingDialog] = useState(false);
  const [confirmationStep, setConfirmationStep] = useState(0);
  const [acknowledgeRisks, setAcknowledgeRisks] = useState(false);
  const [forceTrainingInProgress, setForceTrainingInProgress] = useState(false);
  
  const handleStartAutoTrain = async () => {
    if (!selectedInfluencer) return;
    
    setAutoTraining(true);
    
    try {
      // Start the complete training pipeline
      const result = await TrainingService.startTrainingPipeline(
        selectedInfluencer,
        autotrainerSettings
      );
      
      if (result.success) {
        // Send webhook notification for monitoring (optional, don't fail if it doesn't work)
        try {
          await WebhookService.sendAutotrainNotification(selectedInfluencer, autotrainerSettings);
        } catch (webhookError) {
          console.warn('Webhook notification failed (non-critical):', webhookError);
        }
        
        // Show success message with job details
        const jobId = result.jobId || result.training?.id || result.training?.output?.id || 'Unknown';
        const successMessage = `✅ Training job submitted successfully!\n\nJob ID: ${jobId}\n\nThe training will run in the background and may take several hours to complete.`;
        
        // Log details for debugging
        console.log('Training pipeline successful:', {
          jobId,
          zip: result.zip,
          training: result.training,
          message: successMessage
        });
        
        // Ensure we show the success message
        setZipSnackbarMessage(successMessage);
        setZipSnackbarSeverity('success');
        setZipSnackbarOpen(true);
      } else {
        // Show error message
        const errorMessage = `❌ ${result.message || 'Failed to start training'}`;
        console.error('Training pipeline failed:', result.error);
        
        setZipSnackbarMessage(errorMessage);
        setZipSnackbarSeverity('error');
        setZipSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error starting training:', error);
      const errorMessage = `❌ Failed to start training: ${error.message}`;
      
      setZipSnackbarMessage(errorMessage);
      setZipSnackbarSeverity('error');
      setZipSnackbarOpen(true);
    } finally {
      setAutoTraining(false);
    }
  };
  
  const handleForceTraining = async () => {
    if (!selectedInfluencer || !acknowledgeRisks) return;
    
    setForceTrainingInProgress(true);
    
    try {
      await api.post('/training/force-train', {
        influencer_id: selectedInfluencer.id,
        settings: autotrainerSettings,
        force_override: true,
        acknowledge_risks: acknowledgeRisks
      });
      
      // Close dialog and show success message
      setForceTrainingDialog(false);
      setZipSnackbarMessage('⚠️ FORCED training initiated - Administrators have been notified');
      setZipSnackbarSeverity('warning');
      setZipSnackbarOpen(true);
      
      // Refresh validation data
      fetchValidationData();
    } catch (error) {
      console.error('Error forcing training:', error);
      setZipSnackbarMessage('Failed to initiate forced training. Please try again.');
      setZipSnackbarSeverity('error');
      setZipSnackbarOpen(true);
    } finally {
      setForceTrainingInProgress(false);
    }
  };
  
  const handleCloseForceDialog = () => {
    setForceTrainingDialog(false);
    setConfirmationStep(0);
    setAcknowledgeRisks(false);
  };

  const handleCloseSnackbar = () => {
    setZipSnackbarOpen(false);
  };

  // Removed handleSubmit since training is triggered by autotrain
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Form submission disabled - training is triggered by autotrain
  };
  
  const fetchValidationData = async () => {
    if (!selectedInfluencer) return;
    
    setValidationLoading(true);
    setValidationError(null);
    
    try {
      const response = await api.post('/training/validate', {
        influencer_id: selectedInfluencer.id
      });
      
      setValidationData(response.data);
    } catch (error) {
      console.error('Error fetching validation data:', error);
      setValidationError('Failed to validate training readiness. Please try again.');
    } finally {
      setValidationLoading(false);
    }
  };
  
  if (!selectedInfluencer) {
    return (
      <Container>
        <Typography variant="h5" component="h2" gutterBottom>
          Please select an influencer to view training settings.
        </Typography>
      </Container>
    );
  }
  
  return (
    <Container>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Training Settings
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ArchiveIcon />}
            onClick={handleStartAutoTrain}
            disabled={!selectedInfluencer || autoTraining}
          >
            {autoTraining ? <CircularProgress size={24} color="inherit" /> : 'AutoTrain'}
          </Button>
        </Box>
      </Box>
      
      {/* Training Validation Status Card */}
      {selectedInfluencer && (
        <Card sx={{ mb: 3, border: validationData && !validationData.ready_for_training ? '2px solid #f44336' : '2px solid #4caf50' }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {validationLoading ? (
                <>
                  <CircularProgress size={20} />
                  Checking Training Readiness...
                </>
              ) : validationData ? (
                <>
                  {validationData.ready_for_training ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <WarningIcon color="error" />
                  )}
                  Training Validation Status
                  <Chip
                    label={validationData.influencer_type === 'vintage' ? 'Vintage Influencer' : 'Standard Influencer'}
                    color={validationData.influencer_type === 'vintage' ? 'secondary' : 'primary'}
                    size="small"
                  />
                </>
              ) : (
                <>
                  <InfoIcon color="info" />
                  Training Validation
                </>
              )}
            </Typography>
            
            {validationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {validationError}
                <Button onClick={fetchValidationData} size="small" sx={{ ml: 1 }}>
                  Retry
                </Button>
              </Alert>
            )}
            
            {validationData && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Image Count: {validationData.image_count} / {validationData.required_threshold} required
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(validationData.threshold_percentage, 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: validationData.ready_for_training ? '#4caf50' : '#f44336'
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {validationData.threshold_percentage.toFixed(1)}% of required images
                  </Typography>
                </Box>
                
                <Alert
                  severity={validationData.ready_for_training ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                >
                  {validationData.ready_for_training
                    ? '✅ Ready for training! All validation checks passed.'
                    : `⚠️ Not ready for training. Need ${validationData.required_threshold - validationData.image_count} more images.`
                  }
                </Alert>
                
                {validationData.recommendations && validationData.recommendations.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Recommendations:
                    </Typography>
                    <List dense>
                      {validationData.recommendations.map((recommendation, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            {recommendation.startsWith('✓') ? (
                              <CheckCircleIcon fontSize="small" color="success" />
                            ) : recommendation.includes('Note:') ? (
                              <InfoIcon fontSize="small" color="info" />
                            ) : (
                              <WarningIcon fontSize="small" color="warning" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={recommendation}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <form onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {/* Autotrainer Settings */}
          <Typography variant="h5" component="h2" gutterBottom>
            Autotrainer Settings
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                name="name"
                label="Name"
                fullWidth
                value={autotrainerSettings.name}
                onChange={handleAutotrainerChange}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="trigger"
                label="Trigger"
                fullWidth
                value={autotrainerSettings.trigger}
                onChange={handleAutotrainerChange}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="validation_prompt"
                label="Validation Prompt"
                fullWidth
                value={autotrainerSettings.validation_prompt}
                onChange={handleAutotrainerChange}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="checkpoint"
                label="Checkpoint"
                fullWidth
                value={autotrainerSettings.checkpoint}
                onChange={handleAutotrainerChange}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="project"
                label="Project"
                fullWidth
                value={autotrainerSettings.project}
                onChange={handleAutotrainerChange}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                name="steps"
                label="Steps"
                type="number"
                fullWidth
                value={autotrainerSettings.steps}
                onChange={handleAutotrainerChange}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                name="rank"
                label="Rank"
                type="number"
                fullWidth
                value={autotrainerSettings.rank}
                onChange={handleAutotrainerChange}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                name="validation_steps"
                label="Validation Steps"
                type="number"
                fullWidth
                value={autotrainerSettings.validation_steps}
                onChange={handleAutotrainerChange}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Resolution</InputLabel>
                <Select
                  name="resolution"
                  value={autotrainerSettings.resolution}
                  onChange={handleAutotrainerChange}
                  disabled={loading}
                >
                  <MenuItem value="512x512">512x512</MenuItem>
                  <MenuItem value="768x768">768x768</MenuItem>
                  <MenuItem value="1024x1024">1024x1024</MenuItem>
                  <MenuItem value="1280x1280">1280x1280</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>LoRA Type</InputLabel>
                <Select
                  name="lora_type"
                  value={autotrainerSettings.lora_type}
                  onChange={handleAutotrainerChange}
                  disabled={loading}
                >
                  <MenuItem value="lycoris">LyCORIS</MenuItem>
                  <MenuItem value="lora">LoRA</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                name="learning_rate"
                label="Learning Rate"
                type="number"
                inputProps={{ step: 0.0001 }}
                fullWidth
                value={autotrainerSettings.learning_rate}
                onChange={handleAutotrainerChange}
                disabled={loading}
                margin="normal"
              />
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 4 }} />
          
          {/* Image Processing Settings */}
          <Typography variant="h5" component="h2" gutterBottom>
            Image Processing Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure automatic image processing for training data preparation.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                name="min_size"
                label="Minimum Size (px)"
                type="number"
                fullWidth
                value={imageProcessingSettings.min_size}
                onChange={handleImageProcessingChange}
                disabled={loading}
                margin="normal"
                helperText="Minimum image dimension for training"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="max_size"
                label="Maximum Size (px)"
                type="number"
                fullWidth
                value={imageProcessingSettings.max_size}
                onChange={handleImageProcessingChange}
                disabled={loading}
                margin="normal"
                helperText="Maximum image dimension for training"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="quality_threshold"
                label="Quality Threshold"
                type="number"
                inputProps={{ step: 0.1, min: 0, max: 1 }}
                fullWidth
                value={imageProcessingSettings.quality_threshold}
                onChange={handleImageProcessingChange}
                disabled={loading}
                margin="normal"
                helperText="Minimum quality score (0.0 - 1.0)"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="caption_template"
                label="Caption Template"
                fullWidth
                value={imageProcessingSettings.caption_template}
                onChange={handleImageProcessingChange}
                disabled={loading}
                margin="normal"
                helperText="Template for automatic image captions"
              />
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      {/* ZIP Creation Snackbar */}
      <Snackbar
        open={zipSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={zipSnackbarMessage}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={zipSnackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {zipSnackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* Force Training Dialog */}
      <Dialog
        open={forceTrainingDialog}
        onClose={handleCloseForceDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#f44336', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon />
          ⚠️ FORCE TRAINING WARNING
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {confirmationStep === 0 && (
            <>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  🚨 POTENTIAL SERVICE DISRUPTION WARNING
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Training with insufficient images may cause:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="• System downtime and service interruptions" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Training failures and wasted computational resources" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Poor model quality and unreliable results" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Potential data corruption or inconsistencies" />
                  </ListItem>
                </List>
              </Alert>
              
              {validationData && (
                <Card sx={{ mb: 3, bgcolor: '#fff3e0' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="warning.main">
                      Current Validation Status:
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      • Image Count: <strong>{validationData.image_count}</strong> (Required: <strong>{validationData.required_threshold}</strong>)
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      • Completion: <strong>{validationData.threshold_percentage.toFixed(1)}%</strong>
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      • Missing Images: <strong>{validationData.required_threshold - validationData.image_count}</strong>
                    </Typography>
                  </CardContent>
                </Card>
              )}
              
              <Typography variant="body1" gutterBottom>
                <strong>Administrators will be automatically notified of this forced training attempt.</strong>
              </Typography>
            </>
          )}
          
          {confirmationStep === 1 && (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Final Confirmation Required
                </Typography>
                <Typography variant="body1">
                  By proceeding, you acknowledge that you understand the risks and take full responsibility for any resulting system issues.
                </Typography>
              </Alert>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledgeRisks}
                    onChange={(e) => setAcknowledgeRisks(e.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2">
                    <strong>I acknowledge the risks and take full responsibility for potential service disruptions,
                    training failures, and poor model quality that may result from forcing training with insufficient data.</strong>
                  </Typography>
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseForceDialog} color="primary">
            Cancel - Return to Safety
          </Button>
          
          {confirmationStep === 0 && (
            <Button
              onClick={() => setConfirmationStep(1)}
              color="warning"
              variant="outlined"
              startIcon={<WarningIcon />}
            >
              I Understand the Risks - Continue
            </Button>
          )}
          
          {confirmationStep === 1 && (
            <Button
              onClick={handleForceTraining}
              color="error"
              variant="contained"
              disabled={!acknowledgeRisks || forceTrainingInProgress}
              startIcon={forceTrainingInProgress ? <CircularProgress size={20} /> : <WarningIcon />}
            >
              {forceTrainingInProgress ? 'Initiating Forced Training...' : '🚨 FORCE TRAINING'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TrainingSettingsPage;
